/**
 * The Fund Register (IP-51, IP-53, IP-109, IP-112; design 2026-10-02 sections
 * 6 and 8), the screen's logic without React, so node --test reads it
 * directly.
 *
 * Everything shown is the Core's register as GET register/... answers it -
 * never the prototype's in-browser book, which nothing filled:
 *
 *   register/funds                 the cards: units in issue (Sigma
 *                                  holder_balance), holders with units, the
 *                                  latest published price and NAV
 *   register/funds/{fund}/holders  a page of the fund's holders (keyset on
 *                                  holder_no), each with youngUnits and
 *                                  oldUnits by the fund's exit-fee holding
 *                                  months today - null when the fund has no
 *                                  fund_config in force
 *   register/holders/{id}          one holder: mappings, balances, lots (each
 *                                  young or old from a date), history
 *   register/transactions          unit_transaction, filtered by fund, holder
 *                                  (its id, or a holder number typed in),
 *                                  txnType, source and dealing date, a page at
 *                                  a time (Load more)
 *   register/history               the INITIAL history (legacy_transaction,
 *                                  IP-109): outside the register's fold, its
 *                                  own table and its own cursor
 *   register/extract               the Rule 37(3) extract, as CSV text (or ?format=xlsx: a link to an Excel file) with
 *                                  its name, saved as a file by the Console
 *
 * A holder's value is units x the fund's latest NAV per unit and its share is
 * units / units in issue, both worked on the decimal strings the API sends
 * (@lfoc/domain/decimal.js) - never through a float - and shown as format.js
 * shows figures. Without a price there is no value; without units in issue,
 * no share: a dash, never 0.
 *
 * The hash route is #transactions/registry[/<fund>][/<view>]. A fund segment
 * is taken only when it looks like a fund code (/^UT[A-Z]{2,7}$/, the API's
 * own test) and then only when GET register/funds lists it; until then, and
 * for any other text, the register opens on every fund. The fund's name is
 * always the API's.
 */
import { isZero, mul, percent, sum, toFixed } from '@lfoc/domain/decimal.js';
import { colomboBusinessDate } from '@lfoc/domain/orders/dealing-date.js';

/** A fund code as the register's reads test it (register/reads.js, pricing/reads.js FUND). */
export const FUND_CODE = /^UT[A-Z]{2,7}$/;

/**
 * The register's views, as the hash names them: holders ('allocation', the
 * name the hash has always used), the unit transactions, and the INITIAL
 * history. A bare #transactions/registry/<view> is every fund.
 */
export const VIEWS = Object.freeze(['allocation', 'transactions', 'history']);

/** A page of holders, transactions or history (the reads take 1 to 200). */
export const PAGE = 100;

/** unit_transaction.txn_type (0005, 0012; register/reads.js TXN_TYPES), in words. */
export const TXN_TYPE_LABEL = Object.freeze({
    SUBSCRIPTION: 'Subscription',
    REDEMPTION: 'Redemption',
    SWITCH_IN: 'Switch in',
    SWITCH_OUT: 'Switch out',
    TRANSFER_IN: 'Transfer in',
    TRANSFER_OUT: 'Transfer out',
    DIST_REINVEST: 'Distribution reinvested',
    CONVERSION: 'Conversion (opening lot)',
    ADJUSTMENT: 'Adjustment',
    REVERSAL: 'Reversal'
});

export const TXN_TYPE_COLOUR = Object.freeze({
    SUBSCRIPTION: 'green', SWITCH_IN: 'blue', TRANSFER_IN: 'blue', DIST_REINVEST: 'green', CONVERSION: 'purple',
    REDEMPTION: 'red', SWITCH_OUT: 'orange', TRANSFER_OUT: 'orange', ADJUSTMENT: 'default', REVERSAL: 'default'
});

/** unit_transaction.source (0012 adds UPLOAD, a DAILY transactions file; register/reads.js TXN_SOURCES), in words. */
export const SOURCE_LABEL = Object.freeze({
    LIV: 'LIV transaction',
    ATRAD: 'ATRAD',
    INSTRUMENT: 'Instrument',
    CONVERSION: 'Initial load',
    DISTRIBUTION: 'Distribution',
    CORRECTION: 'Correction',
    UPLOAD: 'Daily upload'
});

/** LIV's TYPE of an INITIAL history row (legacy_transaction.txn_type; register/reads.js LEGACY_TXN_TYPES). */
export const LEGACY_TYPE_LABEL = Object.freeze({ S: 'Subscription', R: 'Redemption', SS: 'Switch / transfer in', SR: 'Switch / transfer out' });

export const LEGACY_TYPE_COLOUR = Object.freeze({ S: 'green', R: 'red', SS: 'blue', SR: 'orange' });

/** A lot's provenance (lot.provenance; 0012 adds UPLOAD), in words. */
export const PROVENANCE_LABEL = Object.freeze({
    SUBSCRIPTION: 'Subscription', SWITCH_IN: 'Switch in', TRANSFER_IN: 'Transfer in', DIST_REINVEST: 'Distribution',
    CONVERSION: 'Initial load', UPLOAD: 'Daily upload'
});

const options = (labels) => Object.entries(labels).map(([value, label]) => ({ value, label }));

export const TXN_TYPE_OPTIONS = options(TXN_TYPE_LABEL);
export const SOURCE_OPTIONS = options(SOURCE_LABEL);
export const LEGACY_TYPE_OPTIONS = Object.keys(LEGACY_TYPE_LABEL).map((value) => ({ value, label: value + ' · ' + LEGACY_TYPE_LABEL[value] }));

/** Today in Asia/Colombo (the register's business clock), YYYY-MM-DD. */
export const colomboToday = (at = new Date()) => colomboBusinessDate(at);

/**
 * The route the hash asks for: a fund segment that looks like a fund code,
 * and a view. A fund is not known to exist until resolveRoute has checked it
 * against GET register/funds.
 *
 * @param {string} hash window.location.hash, e.g. '#transactions/registry/UTIF/transactions'
 * @returns {{ fundId: string|null, view: string }}
 */
export function routeFromHash(hash) {
    const [, , second = '', third = ''] = String(hash ?? '').replace(/^#/, '').split('/');
    const fundId = FUND_CODE.test(second) ? second : null;
    const view = fundId ? third : second;
    return { fundId, view: VIEWS.includes(view) ? view : VIEWS[0] };
}

/** The hash for a route: #transactions/registry, /<view>, /<fund>/<view>. */
export function hashFor(fundId, view) {
    const shown = VIEWS.includes(view) ? view : VIEWS[0];
    if (fundId) return 'transactions/registry/' + fundId + '/' + shown;
    return shown === VIEWS[0] ? 'transactions/registry' : 'transactions/registry/' + shown;
}

/**
 * The route checked against the funds the API lists: a fund it does not
 * list is dropped (and named, so the page can say so). Before the list has
 * loaded (funds null), nothing is dropped and nothing said.
 *
 * @returns {{ fundId: string|null, view: string, unknownFund: string|null }}
 */
export function resolveRoute(route, funds) {
    if (!route.fundId || funds === null || funds === undefined) return { ...route, unknownFund: null };
    const listed = funds.some((f) => f.fundId === route.fundId);
    return listed ? { ...route, unknownFund: null } : { fundId: null, view: route.view, unknownFund: route.fundId };
}

/** The fund the API lists under this code, or null. */
export const fundOf = (funds, fundId) => (fundId ? (funds ?? []).find((f) => f.fundId === fundId) ?? null : null);

/** The fund's name as the API gives it. */
export const fundName = (fund) => fund?.name ?? fund?.shortName ?? fund?.fundId ?? '';

const present = (value) => value !== null && value !== undefined && value !== '';

/** units x NAV per unit, 2 dp, or null without both. */
export function valueOf(units, navPerUnit) {
    if (!present(units) || !present(navPerUnit)) return null;
    return toFixed(mul(units, navPerUnit), 2);
}

/** units / units in issue as a percentage (4 dp), or null when there is nothing to divide by. */
export function shareOf(units, unitsInIssue) {
    if (!present(units) || !present(unitsInIssue) || isZero(unitsInIssue)) return null;
    return percent(units, unitsInIssue, 4);
}

/**
 * A page of GET register/funds/{fund}/holders as the table shows it: each
 * holder with its value at the fund's latest NAV per unit and its share of
 * the units in issue.
 *
 * @param {object[]} holders the page's holders
 * @param {object|null} fund the fund's card (GET register/funds)
 */
export function holderRows(holders, fund, funds = []) {
    return (holders ?? []).map((h) => {
        // One fund chosen: its price. Every fund (GET register/allocation): each row's own fund, from the cards.
        const own = fund ?? (funds ?? []).find((f) => f.fundId === h.fundId) ?? null;
        return {
            ...h,
            // A holder holding several funds is a row per fund, so the key names both.
            key: h.holderId + (h.fundId ? '|' + h.fundId : ''),
            value: valueOf(h.units, own?.price?.navPerUnit ?? null),
            share: shareOf(h.units, own?.unitsInIssue ?? null)
        };
    });
}

/** Whether rows come from more than one fund - their units cannot be added together, only their LKR. */
const manyFunds = (rows) => new Set((rows ?? []).map((r) => r.fundId).filter(Boolean)).size > 1;

/**
 * Units and value of the holders the table is showing, summed on the decimal
 * strings (never a float). amount is null when no row carries a value - a
 * dash, never 0. Empty is 0 units.
 */
export function rowTotals(rows) {
    const units = [];
    const amounts = [];
    for (const row of rows ?? []) {
        if (present(row.units)) units.push(row.units);
        if (present(row.value)) amounts.push(row.value);
    }
    return {
        // Units of different funds are different things: no total across funds (a dash), only the LKR.
        units: manyFunds(rows) ? null : toFixed(sum(units), 4),
        amount: amounts.length ? toFixed(sum(amounts), 2) : null
    };
}

/**
 * IP-158: an account's name, from its strategies' holder names - the one name
 * they share; else what they all begin with, cut back to a whole word and
 * any separator ("Example Holdings Treasury - Capital Growth" and "Example
 * Holdings Treasury - Treasury Reserve" are "Example Holdings Treasury");
 * else the first.
 */
export function accountName(names) {
    const list = [...new Set((names ?? []).filter(Boolean))];
    if (list.length <= 1) return list[0] ?? '';
    let prefix = list[0];
    for (const name of list.slice(1)) {
        let i = 0;
        while (i < prefix.length && i < name.length && prefix[i] === name[i]) i += 1;
        prefix = prefix.slice(0, i);
    }
    const cut = prefix.replace(/\S*$/, '').replace(/[\s\-–:,(]+$/, '');
    return cut.length >= 2 ? cut : list[0];
}

/** The decimal sum of a field over rows, at the scale, or null when no row carries it - a dash, never 0. */
function sumOf(rows, field, scale) {
    const values = rows.map((r) => r[field]).filter(present);
    return values.length ? toFixed(sum(values), scale) : null;
}

/**
 * IP-158: the fund's holders as the table shows them - by client account,
 * each account a row with its strategies (its holders, holderRows' rows)
 * under it in `strategies`. An account's units, value, share, cost and young
 * and old units are its strategies' sums, so the figures on the account's
 * row add up to the rows under it. Accounts and strategies keep the API's
 * order (by account, then holder number); a holder the API names no account
 * for is an account of one. The key is the account's first holder's id, so
 * no holder number reaches the page (the drawer shows it).
 *
 * @param {object[]} rows holderRows' rows
 * @returns {object[]} { key, accountKey, accountCode, fullName, units, value, share, costValue, youngUnits, oldUnits, strategies }
 */
export function accountRows(rows) {
    const groups = new Map();
    for (const row of rows ?? []) {
        const accountKey = row.accountKey ?? row.holderId;
        if (!groups.has(accountKey)) groups.set(accountKey, []);
        groups.get(accountKey).push(row);
    }
    return [...groups].map(([accountKey, strategies]) => {
        // An account across several funds (every fund shown): its LKR adds up, its units and share do not.
        const mixed = manyFunds(strategies);
        return {
            key: 'account:' + strategies[0].holderId,
            accountKey,
            accountCode: strategies[0].accountCode ?? null,
            fullName: accountName(strategies.map((s) => s.fullName)),
            units: mixed ? null : sumOf(strategies, 'units', 4) ?? '0.0000',
            value: sumOf(strategies, 'value', 2),
            share: mixed ? null : sumOf(strategies, 'share', 4),
            costValue: sumOf(strategies, 'costValue', 2),
            youngUnits: mixed ? null : sumOf(strategies, 'youngUnits', 4),
            oldUnits: mixed ? null : sumOf(strategies, 'oldUnits', 4),
            // The funds it holds, for the Fund column of an account row.
            funds: [...new Set(strategies.map((x) => x.fundId).filter(Boolean))],
            strategies
        };
    });
}

/**
 * IP-158: the line under the holders - how many client accounts the loaded
 * strategies belong to, and how many of the fund's strategies with units
 * (its card's holderCount) are loaded.
 */
export function holdersShown(holders, holderCount = null) {
    const loaded = holders ?? [];
    const accounts = new Set(loaded.map((h) => h.accountKey ?? h.holderId)).size;
    const total = holderCount ?? loaded.length;
    return accounts + ' account' + (accounts === 1 ? '' : 's') + ', ' + loaded.length + ' of ' + total
        + ' strateg' + (total === 1 ? 'y' : 'ies') + ' shown';
}

/** A share as the Progress bar draws it (a number, 2 dp), or 0 for none. */
export const sharePercent = (share) => (share === null ? 0 : Number(toFixed(share, 2)));

/** A query without the filters left empty (endpoints.js queryString drops them too, but the tests read it). */
function compact(query) {
    return Object.fromEntries(Object.entries(query).filter(([, v]) => v !== undefined && v !== null && v !== ''));
}

/** GET register/funds/{fund}/holders's query, and GET register/allocation's (every fund). */
export const holdersQuery = ({ cursor = null } = {}) => compact({ limit: PAGE, cursor });

/**
 * GET register/transactions's query from the filters: fund, holder (its
 * uuid), a holder number typed into the filter (plain text, matched on the
 * server), txnType and source (unit_transaction's own vocabulary), and a
 * dealing-date range (YYYY-MM-DD strings, both inclusive).
 */
export function transactionsQuery({ fundId = null, holderId = null, holderNo = null, txnType = null, source = null, from = null, to = null, cursor = null } = {}) {
    return compact({ fundId, holderId, holderNo, txnType, source, from, to, limit: PAGE, cursor });
}

/** GET register/transactions/export's query: the same filters as the list's, with no page (the export reads every matching row). */
export function transactionsExportQuery({ fundId = null, holderId = null, holderNo = null, txnType = null, source = null, from = null, to = null } = {}) {
    return compact({ fundId, holderId, holderNo, txnType, source, from, to });
}

/** GET register/history's query: as the transactions', with LIV's TYPE (S, R, SS, SR) and no source. */
export function historyQuery({ fundId = null, holderId = null, txnType = null, from = null, to = null, cursor = null } = {}) {
    return compact({ fundId, holderId, txnType, from, to, limit: PAGE, cursor });
}

/** Whether any filter beyond the fund is set (the Clear filters button). */
export const hasFilters = ({ holder = null, holderNo = null, txnType = null, source = null, from = null, to = null } = {}) =>
    Boolean(holder || holderNo || txnType || source || from || to);

/**
 * A lot's age as the holder drawer says it: young until, or old since, the
 * date its units pass the fund's exit-fee holding months (oldFrom); a dash
 * when the API could not say (the fund has no fund_config in force today).
 *
 * @returns {{ text: string, young: boolean|null }}
 */
export function lotAge(lot) {
    if (lot?.young === null || lot?.young === undefined || !lot?.oldFrom) return { text: '—', young: null };
    return lot.young ? { text: 'Young until ' + lot.oldFrom, young: true } : { text: 'Old since ' + lot.oldFrom, young: false };
}

/** The young/old columns' heading: the period they split at, when the API said it. */
export function ageHeading(holdingMonths) {
    return Number.isSafeInteger(holdingMonths) ? 'held under ' + holdingMonths + ' months' : 'by the fund\'s exit-fee period';
}

/** GET register/extract's query: one fund (or every fund) as at a date (default: the server's today in Colombo). */
export const extractQuery = ({ fundId = null, asAt = null, format = null } = {}) => compact({ fundId, asAt, format });

const EXTRACT_NAME = /^register_extract_(UT[A-Z]{2,7}|ALL)_\d{8}\.csv$/;

/**
 * The extract as a file to save: { fileName, csv }. The server names it
 * register_extract_<FUND|ALL>_<YYYYMMDD>.csv (register/extract.js); any
 * other name is replaced by that one built here, so nothing but a safe name
 * reaches the person's disk.
 *
 * @param {{ fileName?: string, csv: string }} answer GET register/extract
 * @param {{ fundId?: string|null, asAt: string }} asked
 */
export function extractFile(answer, { fundId = null, asAt }) {
    if (typeof answer?.csv !== 'string') throw new Error('The extract came back without its CSV.');
    const fileName = EXTRACT_NAME.test(answer.fileName ?? '')
        ? answer.fileName
        : 'register_extract_' + (fundId ?? 'ALL') + '_' + String(asAt).replace(/-/g, '') + '.csv';
    return { fileName, csv: answer.csv };
}

/** A person as a read names them (<role>Name beside the uid; IP-112), else the uid, else a dash. */
export const person = (name, uid) => name ?? uid ?? '—';
